'use client';

import { useActionState } from 'react';
import Link from 'next/link';
import { ArrowLeft, ArrowRight, Loader2, QrCode, User } from 'lucide-react';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import styles from '../login/page.module.css';

interface ForgotState {
  error?: string;
  success?: string;
}

async function forgotAction(_prev: ForgotState, formData: FormData): Promise<ForgotState> {
  const identity = String(formData.get('login_identity') ?? '').trim();

  if (!identity) {
    return { error: 'Введите логин или email' };
  }

  try {
    const res = await api.post<{ message?: string }>('/api/v1/auth/forgot-password', {
      login_identity: identity,
    });

    return {
      success:
        res.data?.message ||
        res.message ||
        'Если аккаунт существует, мы отправили инструкцию на ваш email.',
    };
  } catch (err) {
    return {
      error: err instanceof Error ? err.message : 'Произошла ошибка. Попробуйте позже.',
    };
  }
}

export default function ForgotPasswordPage() {
  const [state, formAction, pending] = useActionState(forgotAction, {});

  return (
    <div className={styles.page}>
      <div
        className={styles.bgNature}
        style={{ backgroundImage: "url('/nature_bg.png')" }}
      />
      <div className={styles.bgOverlay} />

      <div className="relative w-full max-w-[440px] bg-white/90 border border-white/60 text-slate-900 backdrop-blur-xl shadow-2xl shadow-indigo-950/5 z-10 p-2 rounded-2xl animate-fade-in">
        <div className="flex flex-col space-y-1.5 p-6 text-center pb-4 pt-6">
          <div className="flex items-center justify-center gap-2 mb-1">
            <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-tr from-indigo-600 to-violet-500 rounded-xl text-white shadow-lg shadow-indigo-500/30">
              <QrCode className="h-5 w-5" />
            </div>
            <div className="flex items-baseline text-2xl font-bold tracking-tight">
              <span className="text-slate-900">ucheta</span>
              <span className="text-indigo-600">.ru</span>
            </div>
          </div>
          <div className="text-slate-500 text-xs font-semibold uppercase tracking-wider">
            Восстановление пароля
          </div>
          <p className="text-sm text-slate-500 pt-1">
            Введите email владельца или логин сотрудника — отправим инструкцию на почту.
          </p>
        </div>

        <div className="p-6 pt-0 space-y-4 px-6 pb-6">
          {state.error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs animate-shake">
              <span>⚠</span> {state.error}
            </div>
          )}

          {state.success ? (
            <div className="space-y-4">
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-sm leading-relaxed">
                ✅ {state.success}
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                Владельцу придёт ссылка для создания нового пароля (действует 1 час).
                Сотруднику с привязанным email — новый временный пароль.
              </p>
              <Link
                href="/login"
                className={cn(
                  buttonVariants({ className: 'w-full h-10 gap-1.5 whitespace-nowrap' }),
                )}
                id="link-back-to-login"
              >
                Вернуться на страницу входа
              </Link>
            </div>
          ) : (
            <form action={formAction} className="space-y-4" noValidate>
              <div className="space-y-1.5">
                <Label
                  htmlFor="login_identity"
                  className="text-[11px] font-bold uppercase tracking-wider text-slate-600"
                >
                  Логин или Email
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="login_identity"
                    name="login_identity"
                    type="text"
                    autoComplete="username"
                    placeholder="owner@company.ru или manager_1@10001"
                    className="h-10 pl-10 bg-white/70 border-slate-200 text-slate-900 placeholder:text-placeholder-foreground focus-visible:border-indigo-500 focus-visible:ring-indigo-500/10"
                    required
                    disabled={pending}
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-10 gap-1.5 whitespace-nowrap bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 text-white font-semibold rounded-xl shadow-lg shadow-indigo-600/20 transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50"
                disabled={pending}
                id="btn-forgot-password-submit"
              >
                {pending ? (
                  <>
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                    <span>Отправка...</span>
                  </>
                ) : (
                  <>
                    <span>Отправить инструкцию</span>
                    <ArrowRight className="h-4 w-4 shrink-0" />
                  </>
                )}
              </Button>

              <div className="text-center pt-2">
                <Link
                  href="/login"
                  className={cn(
                    buttonVariants({
                      variant: 'link',
                      className: 'text-xs text-indigo-600 hover:text-indigo-500 gap-1.5 whitespace-nowrap',
                    }),
                  )}
                >
                  <ArrowLeft className="h-3 w-3 shrink-0" />
                  <span>Назад ко входу</span>
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
